import { z } from "zod";
/**
 * The focus areas a member can be working on, one taxonomy for all five
 * behaviours. Restored with the level system that reads them: a level is five
 * distinct practice days, and a habit is established at twenty-one.
 */
export const focusAreaIdSchema = z.enum([
    // Sleep
    "winding-down",
    "wake-rhythm",
    "bedroom-environment",
    // Food
    "steady-glucose",
    "lighter-sodium",
    "balanced-plate",
    "protein-first",
    "more-veg",
    "meal-rhythm",
    "mindful-eating",
    "hydration",
    // Movement
    "moving-more",
    "everyday-activity",
    "sitting-breaks",
    "after-meal-movement",
    "building-strength",
    "getting-outside",
    // Stress / recovery
    "active-recovery",
    "downshifting",
    "releasing-tension",
    "taking-breaks",
    "mind-checkins",
    "screen-free-calm",
    // Connection
    "staying-connected",
    "reaching-out",
    "quality-time",
    "asking-for-support",
    // Vitals
    "blood-pressure",
    "heart-recovery",
    // Weight / medication
    "weight-trend",
    "glp1-support",
]);
/**
 * The full taxonomy, keyed by id. Order here is the display order used when a
 * surface wants a stable ordering that is not driven by member data.
 */
export const FOCUS_AREAS = {
    // ── Sleep ────────────────────────────────────────────────────────────────
    "winding-down": {
        id: "winding-down",
        label: "Winding down at night",
        pillar: "sleep",
        why: "A calmer runway into the evening helps you fall asleep faster.",
    },
    "wake-rhythm": {
        id: "wake-rhythm",
        label: "Protecting your wake time",
        pillar: "sleep",
        why: "A steady wake time and morning light anchor your whole body clock.",
    },
    "bedroom-environment": {
        id: "bedroom-environment",
        label: "A restful bedroom",
        pillar: "sleep",
        why: "A dark, cool, quiet room makes deeper sleep easier to reach.",
    },
    // ── Food ─────────────────────────────────────────────────────────────────
    "steady-glucose": {
        id: "steady-glucose",
        label: "Eating for steady glucose",
        pillar: "food",
        why: "Small changes around meals keep your glucose on an even keel.",
    },
    "lighter-sodium": {
        id: "lighter-sodium",
        label: "Lighter on salt",
        pillar: "food",
        why: "Trimming sodium eases the load on your heart and blood pressure.",
    },
    "balanced-plate": {
        id: "balanced-plate",
        label: "A plate that keeps you full",
        pillar: "food",
        why: "A balanced plate that keeps you full steadies energy for hours.",
    },
    "protein-first": {
        id: "protein-first",
        label: "Protein at every meal",
        pillar: "food",
        why: "Protein at each meal steadies appetite and protects your muscle.",
    },
    "more-veg": {
        id: "more-veg",
        label: "More vegetables and fibre",
        pillar: "food",
        why: "More vegetables and fibre fill you up and feed your gut.",
    },
    "meal-rhythm": {
        id: "meal-rhythm",
        label: "A regular meal rhythm",
        pillar: "food",
        why: "Eating at regular times keeps hunger and energy even.",
    },
    "mindful-eating": {
        id: "mindful-eating",
        label: "Eating with attention",
        pillar: "food",
        why: "Slowing down at meals helps you notice when you are full.",
    },
    hydration: {
        id: "hydration",
        label: "Staying hydrated",
        pillar: "food",
        why: "Regular fluids keep your energy and focus steady through the day.",
    },
    // ── Movement ───────────────────────────────────────────────────────────────
    "moving-more": {
        id: "moving-more",
        label: "Getting your steps in",
        pillar: "movement",
        why: "Frequent easy walks add up to more than one big workout.",
    },
    "everyday-activity": {
        id: "everyday-activity",
        label: "Movement in the gaps",
        pillar: "movement",
        why: "Stairs, errands on foot and the longer way turn routine into exercise.",
    },
    "sitting-breaks": {
        id: "sitting-breaks",
        label: "Breaking up sitting",
        pillar: "movement",
        why: "Standing and moving through long sits keeps body and focus fresh.",
    },
    "after-meal-movement": {
        id: "after-meal-movement",
        label: "Moving after meals",
        pillar: "movement",
        why: "A short move after eating steadies energy and digestion.",
    },
    "building-strength": {
        id: "building-strength",
        label: "Building strength",
        pillar: "movement",
        why: "Regular resistance work protects strength and metabolism over time.",
    },
    "getting-outside": {
        id: "getting-outside",
        label: "Getting outside",
        pillar: "movement",
        why: "Time outdoors lifts your mood and helps set your body clock.",
    },
    // ── Stress / recovery ──────────────────────────────────────────────────────
    "active-recovery": {
        id: "active-recovery",
        label: "Recovery and rest days",
        pillar: "stress",
        why: "Easy days are when the work you have already done actually lands.",
    },
    downshifting: {
        id: "downshifting",
        label: "Breathing and downshifting",
        pillar: "stress",
        why: "A few slow breaths settle your nervous system in minutes.",
    },
    "releasing-tension": {
        id: "releasing-tension",
        label: "Releasing tension",
        pillar: "stress",
        why: "Letting go of held tension eases the load your body carries.",
    },
    "taking-breaks": {
        id: "taking-breaks",
        label: "Taking real breaks",
        pillar: "stress",
        why: "Short breaks in the day keep pressure from building up.",
    },
    "mind-checkins": {
        id: "mind-checkins",
        label: "Checking in with yourself",
        pillar: "stress",
        why: "Noticing how you feel is the first step to tending to it.",
    },
    "screen-free-calm": {
        id: "screen-free-calm",
        label: "Screen-free calm",
        pillar: "stress",
        why: "Less screen time late helps your mind settle down.",
    },
    // ── Connection ─────────────────────────────────────────────────────────────
    "staying-connected": {
        id: "staying-connected",
        label: "Staying connected",
        pillar: "connection",
        why: "Small moments with people you care about buffer a hard day.",
    },
    "reaching-out": {
        id: "reaching-out",
        label: "Reaching out first",
        pillar: "connection",
        why: "Being the one to reach out keeps your circle close.",
    },
    "quality-time": {
        id: "quality-time",
        label: "Time that counts",
        pillar: "connection",
        why: "Unhurried, undistracted time with someone restores you both.",
    },
    "asking-for-support": {
        id: "asking-for-support",
        label: "Reaching out for support",
        pillar: "connection",
        why: "Naming what you need, out loud, is its own kind of strength.",
    },
    // ── Vitals ─────────────────────────────────────────────────────────────────
    "blood-pressure": {
        id: "blood-pressure",
        label: "Keeping blood pressure steady",
        pillar: "movement",
        why: "Movement, salt and stress all nudge your blood pressure.",
    },
    "heart-recovery": {
        id: "heart-recovery",
        label: "Heart and recovery",
        pillar: "stress",
        why: "Resting heart rate and breathing show how recovered you are.",
    },
    // ── Weight / medication ──────────────────────────────────────────────────────
    "weight-trend": {
        id: "weight-trend",
        label: "Tracking your weight trend",
        pillar: "food",
        why: "Watching the trend, not the daily number, is what tells the story.",
    },
    "glp1-support": {
        id: "glp1-support",
        label: "Getting the most from your medication",
        pillar: "food",
        why: "Protein, fluids and gentle movement make GLP-1 treatment easier.",
    },
};
export function focusAreaMeta(id) {
    return FOCUS_AREAS[id];
}
/** Five distinct days of practice is a level. */
export const FOCUS_AREA_DAYS_PER_LEVEL = 5;
/** Twenty-one distinct days is the habit-formation threshold. */
export const HABIT_TARGET_DAYS = 21;
export const habitStageSchema = z.enum(["forming", "building", "established"]);
export function habitStage(distinctDays) {
    if (distinctDays >= HABIT_TARGET_DAYS)
        return "established";
    if (distinctDays >= 7)
        return "building";
    return "forming";
}
/** Level from the days the member actually practised, never from a calendar. */
export function focusAreaLevelProgress(distinctDays) {
    const totalDays = Math.max(0, Math.floor(distinctDays) || 0);
    return {
        level: Math.floor(totalDays / FOCUS_AREA_DAYS_PER_LEVEL) + 1,
        daysPerLevel: FOCUS_AREA_DAYS_PER_LEVEL,
        daysThisLevel: totalDays % FOCUS_AREA_DAYS_PER_LEVEL,
        habitEstablished: totalDays >= HABIT_TARGET_DAYS,
    };
}
//# sourceMappingURL=focus-areas.js.map